import { Trash2, Plus } from "lucide-react";

const CATEGORIES = [
  "Flex Printing", "Vinyl Printing", "One Way Vision", "ACP Sign Boards", "Glow Sign Boards",
  "Acrylic Letters", "LED Sign Boards", "Shop Branding", "Vehicle Branding", "Banners",
  "Posters", "Visiting Cards", "Letterheads", "Flyers", "Brochures", "Corporate Gifts",
  "T-Shirts", "Caps", "Mugs", "ID Cards", "Stickers", "Roll Up Standee",
  "Promotional Materials", "Sunboard", "Foam Board",
];

const GST_OPTIONS = [0, 5, 12, 18, 28];
// Addon templates — rates are ALWAYS user-defined; no fixed prices baked in.
const ADDON_TEMPLATES = ["Wooden Frame", "Metal Frame", "Metal Standee"];

const emptyItem = () => ({
  product_category: "Flex Printing",
  product_name: "",
  material: "",
  printing_type: "",
  quantity: 1,
  gst_rate: 18,
  gst_mode: "excluded",  // excluded | included | none
  sizes: [{ width: 0, height: 0, unit: "ft", quantity: 1, rate: 0 }],
  addons: [],
});

const calcSizeAmt = (s) => {
  const w = Number(s.width) || 0, h = Number(s.height) || 0;
  const q = Number(s.quantity) || 1, r = Number(s.rate) || 0;
  return (w > 0 && h > 0) ? w * h * q * r : q * r;
};
const calcAddonAmt = (a) => (Number(a.quantity) || 1) * (Number(a.rate) || 0);

export function calcItemTotals(item) {
  const gross = (item.sizes || []).reduce((s, sz) => s + calcSizeAmt(sz), 0)
    + (item.addons || []).reduce((s, a) => s + calcAddonAmt(a), 0);
  const rate = Number(item.gst_rate) || 0;
  const mode = item.gst_mode || "excluded";
  if (mode === "none" || rate <= 0) return { subtotal: gross, tax: 0, total: gross };
  if (mode === "included") {
    const sub = gross * 100 / (100 + rate);
    return { subtotal: sub, tax: gross - sub, total: gross };
  }
  // excluded
  const tax = gross * rate / 100;
  return { subtotal: gross, tax, total: gross + tax };
}

export function calcExtra(charge, rate, mode) {
  const c = Number(charge) || 0;
  const r = Number(rate) || 0;
  if (c <= 0) return { base: 0, tax: 0, total: 0 };
  if (mode === "none" || r <= 0) return { base: c, tax: 0, total: c };
  if (mode === "included") {
    const base = c * 100 / (100 + r);
    return { base, tax: c - base, total: c };
  }
  const tax = c * r / 100;
  return { base: c, tax, total: c + tax };
}

export default function OrderItemsEditor({ items, setItems, testIdPrefix = "item" }) {
  const patchItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const patchSize = (i, si, patch) => setItems(items.map((it, x) => {
    if (x !== i) return it;
    const sizes = it.sizes.map((s, y) => (y === si ? { ...s, ...patch } : s));
    return { ...it, sizes };
  }));
  const patchAddon = (i, ai, patch) => setItems(items.map((it, x) => {
    if (x !== i) return it;
    const addons = it.addons.map((a, y) => (y === ai ? { ...a, ...patch } : a));
    return { ...it, addons };
  }));
  const addItem = () => setItems([...items, emptyItem()]);
  const rmItem = (i) => setItems(items.filter((_, x) => x !== i));
  const addSize = (i) => patchItem(i, { sizes: [...items[i].sizes, { width: 0, height: 0, unit: "ft", quantity: 1, rate: 0 }] });
  const rmSize = (i, si) => patchItem(i, { sizes: items[i].sizes.filter((_, y) => y !== si) });
  const addAddon = (i, name = "") => patchItem(i, { addons: [...(items[i].addons || []), { name, quantity: 1, rate: 0 }] });
  const rmAddon = (i, ai) => patchItem(i, { addons: items[i].addons.filter((_, y) => y !== ai) });

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-editor`}>
      {items.map((it, i) => {
        const t = calcItemTotals(it);
        return (
          <div key={i} className="border-2 border-[hsl(var(--border))] p-4 space-y-3" data-testid={`${testIdPrefix}-${i}`}>
            <div className="flex items-center justify-between">
              <div className="overline text-[#FF6B00]">Item {i + 1}</div>
              {items.length > 1 && (
                <button type="button" onClick={() => rmItem(i)} className="p-1 hover:text-red-500" data-testid={`${testIdPrefix}-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <select value={it.product_category} onChange={(e) => patchItem(i, { product_category: e.target.value })}
                className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none" data-testid={`${testIdPrefix}-${i}-cat`}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input placeholder="Product name" value={it.product_name}
                onChange={(e) => patchItem(i, { product_name: e.target.value })}
                className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none" data-testid={`${testIdPrefix}-${i}-name`} />
              <input placeholder="Material (opt)" value={it.material}
                onChange={(e) => patchItem(i, { material: e.target.value })}
                className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none" />
              <input placeholder="Printing type" value={it.printing_type}
                onChange={(e) => patchItem(i, { printing_type: e.target.value })}
                className="border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="overline">Sizes</div>
                <button type="button" onClick={() => addSize(i)} className="text-xs text-[#FF6B00] hover:underline inline-flex items-center gap-1" data-testid={`${testIdPrefix}-${i}-add-size`}>
                  <Plus size={12} /> Add size
                </button>
              </div>
              {(it.sizes || []).map((s, si) => {
                const amt = calcSizeAmt(s);
                return (
                  <div key={si} className="grid grid-cols-12 gap-2 items-center text-xs">
                    <input type="number" step="0.01" placeholder="W" value={s.width}
                      onChange={(e) => patchSize(i, si, { width: e.target.value })}
                      className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none" data-testid={`${testIdPrefix}-${i}-size-${si}-w`} />
                    <input type="number" step="0.01" placeholder="H" value={s.height}
                      onChange={(e) => patchSize(i, si, { height: e.target.value })}
                      className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none" data-testid={`${testIdPrefix}-${i}-size-${si}-h`} />
                    <select value={s.unit} onChange={(e) => patchSize(i, si, { unit: e.target.value })}
                      className="col-span-1 border border-[hsl(var(--border))] bg-transparent px-1 py-1 outline-none">
                      <option value="ft">ft</option><option value="in">in</option>
                      <option value="m">m</option><option value="cm">cm</option>
                    </select>
                    <input type="number" min="1" placeholder="Qty" value={s.quantity}
                      onChange={(e) => patchSize(i, si, { quantity: e.target.value })}
                      className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none" data-testid={`${testIdPrefix}-${i}-size-${si}-qty`} />
                    <input type="number" step="0.01" placeholder="Rate" value={s.rate}
                      onChange={(e) => patchSize(i, si, { rate: e.target.value })}
                      className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none" data-testid={`${testIdPrefix}-${i}-size-${si}-rate`} />
                    <div className="col-span-2 text-right font-mono">₹{Math.round(amt).toLocaleString("en-IN")}</div>
                    <button type="button" onClick={() => rmSize(i, si)} disabled={it.sizes.length === 1}
                      className="col-span-1 text-muted-foreground hover:text-red-500 disabled:opacity-30">
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Add-ons (rate is fully editable)</div>
                <div className="flex flex-wrap gap-1">
                  {ADDON_TEMPLATES.map((name) => (
                    <button key={name} type="button" onClick={() => addAddon(i, name)}
                      className="text-[10px] uppercase tracking-wide border border-[#FF6B00]/40 text-[#FF6B00] px-2 py-1 hover:bg-[#FF6B00] hover:text-white"
                      data-testid={`${testIdPrefix}-${i}-add-addon-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                      + {name}
                    </button>
                  ))}
                  <button type="button" onClick={() => addAddon(i, "")}
                    className="text-[10px] uppercase tracking-wide border border-[hsl(var(--border))] px-2 py-1 hover:border-[#FF6B00]"
                    data-testid={`${testIdPrefix}-${i}-add-custom-addon`}>
                    + Custom
                  </button>
                </div>
              </div>
              {(it.addons || []).length > 0 && (
                <div className="space-y-1">
                  {it.addons.map((a, ai) => (
                    <div key={ai} className="grid grid-cols-12 gap-2 items-center text-xs">
                      <input placeholder="Add-on name" value={a.name}
                        onChange={(e) => patchAddon(i, ai, { name: e.target.value })}
                        className="col-span-5 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none"
                        data-testid={`${testIdPrefix}-${i}-addon-${ai}-name`} />
                      <input type="number" min="1" placeholder="Qty" value={a.quantity}
                        onChange={(e) => patchAddon(i, ai, { quantity: e.target.value })}
                        className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none"
                        data-testid={`${testIdPrefix}-${i}-addon-${ai}-qty`} />
                      <input type="number" step="0.01" placeholder="Rate" value={a.rate}
                        onChange={(e) => patchAddon(i, ai, { rate: e.target.value })}
                        className="col-span-2 border border-[hsl(var(--border))] bg-transparent px-2 py-1 outline-none"
                        data-testid={`${testIdPrefix}-${i}-addon-${ai}-rate`} />
                      <div className="col-span-2 text-right font-mono">₹{Math.round(calcAddonAmt(a)).toLocaleString("en-IN")}</div>
                      <button type="button" onClick={() => rmAddon(i, ai)} className="col-span-1 text-muted-foreground hover:text-red-500">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 border-t border-[hsl(var(--border))] pt-3 items-end">
              <div className="md:col-span-2">
                <div className="overline mb-1">GST Treatment</div>
                <select value={it.gst_mode || "excluded"} onChange={(e) => patchItem(i, { gst_mode: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none" data-testid={`${testIdPrefix}-${i}-gst-mode`}>
                  <option value="excluded">Add GST on top</option>
                  <option value="included">GST already included</option>
                  <option value="none">No GST</option>
                </select>
              </div>
              <div>
                <div className="overline mb-1">Rate</div>
                <select value={it.gst_rate} onChange={(e) => patchItem(i, { gst_rate: Number(e.target.value) })}
                  disabled={it.gst_mode === "none"}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-2 py-1.5 text-sm outline-none disabled:opacity-40" data-testid={`${testIdPrefix}-${i}-gst`}>
                  {GST_OPTIONS.map((g) => <option key={g} value={g}>{g}%</option>)}
                </select>
              </div>
              <div className="md:col-span-3 grid grid-cols-3 gap-2 text-xs">
                <div><div className="overline">Sub</div><div className="font-mono">₹{Math.round(t.subtotal).toLocaleString("en-IN")}</div></div>
                <div><div className="overline">GST</div><div className="font-mono">₹{Math.round(t.tax).toLocaleString("en-IN")}</div></div>
                <div><div className="overline">Total</div><div className="font-mono font-bold text-[#FF6B00]">₹{Math.round(t.total).toLocaleString("en-IN")}</div></div>
              </div>
            </div>
          </div>
        );
      })}

      <button type="button" onClick={addItem} className="btn-swiss btn-outline" data-testid={`${testIdPrefix}-add`}>
        <Plus size={14} /> Add another product
      </button>
    </div>
  );
}

export { emptyItem };
